import {
  AlertTriangle,
  ArrowRight,
  KeyRound,
  Search,
  X,
} from "lucide-react";
import type { ErrorInfo, FormEvent, ReactNode } from "react";
import { Component, lazy, Suspense, useEffect, useReducer, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { DashboardRequestError, fetchBootstrap } from "./api.js";
import { getAuthClient, neonAuthConfigured, rememberAuthResult, waitForAuthToken } from "./auth.js";
import { I18nConsumer, LanguageSwitcher, useI18n } from "./i18n.js";
import { BrandLogo, ButtonLink, SectionLabel, StatusPill, Surface } from "./PlatformChrome.js";
import type { PlatformBootstrap, UsageSummary } from "./types.js";
import { cn } from "./utils.js";

const DashboardApp = lazy(() =>
  import("./DashboardApp.js").then((module) => ({
    default: module.DashboardApp,
  })),
);

const PublicHome = lazy(() =>
  import("./PublicHome.js").then((module) => ({
    default: module.PublicHome,
  })),
);

const adminIntegerFormatOptions = { maximumFractionDigits: 0 } satisfies Intl.NumberFormatOptions;
const adminCompactIntegerFormatOptions = {
  maximumFractionDigits: 1,
  notation: "compact",
} satisfies Intl.NumberFormatOptions;
const adminDateTimeFormatOptions = {
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  month: "short",
} satisfies Intl.DateTimeFormatOptions;
type AdminIntlLocale = "en-US" | "zh-CN";

const adminIntegerFormatters: Record<AdminIntlLocale, Intl.NumberFormat> = {
  "en-US": new Intl.NumberFormat("en-US", adminIntegerFormatOptions),
  "zh-CN": new Intl.NumberFormat("zh-CN", adminIntegerFormatOptions),
};
const adminCompactIntegerFormatters: Record<AdminIntlLocale, Intl.NumberFormat> = {
  "en-US": new Intl.NumberFormat("en-US", adminCompactIntegerFormatOptions),
  "zh-CN": new Intl.NumberFormat("zh-CN", adminCompactIntegerFormatOptions),
};
const adminDateTimeFormatters: Record<AdminIntlLocale, Intl.DateTimeFormat> = {
  "en-US": new Intl.DateTimeFormat("en-US", adminDateTimeFormatOptions),
  "zh-CN": new Intl.DateTimeFormat("zh-CN", adminDateTimeFormatOptions),
};

function resolveAdminIntlLocale(intlLocale: string): AdminIntlLocale {
  return intlLocale === "zh-CN" ? "zh-CN" : "en-US";
}

function PublicHomeLoadingScreen() {
  return (
    <div aria-busy="true" className="min-h-[100dvh] bg-slate-950 p-6 text-white">
      <div className="mx-auto grid min-h-[calc(100dvh-48px)] w-full max-w-[1180px] grid-rows-[auto_1fr] gap-16">
        <div className="flex items-center justify-between gap-4">
          <BrandLogo variant="horizontal" />
          <span className="h-10 w-28 rounded-md bg-white/10" />
        </div>
        <div className="grid items-center gap-10 lg:grid-cols-[.9fr_1.1fr]">
          <div className="grid gap-4">
            <span className="h-4 w-56 rounded-full bg-white/10" />
            <span className="h-12 w-full max-w-xl rounded-full bg-white/15" />
            <span className="h-12 w-full max-w-md rounded-full bg-white/10" />
            <span className="mt-4 h-11 w-36 rounded-md bg-white/15" />
          </div>
          <div className="hidden min-h-[420px] rounded-lg border border-white/10 bg-white/5 lg:block" />
        </div>
      </div>
    </div>
  );
}

function protectedLoginPath(): string {
  return "/login";
}

function GoogleLogoIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" className={className} focusable="false" viewBox="0 0 24 24">
      <path d="M21.8 12.2c0-.7-.1-1.3-.2-1.9H12v3.6h5.5a4.7 4.7 0 0 1-2 3.1v2.4h3.2c1.9-1.7 3.1-4.2 3.1-7.2Z" fill="#4285F4" />
      <path d="M12 22c2.7 0 4.9-.9 6.6-2.5L15.4 17c-.9.6-2 .9-3.4.9a5.9 5.9 0 0 1-5.6-4.1H3.1v2.5A10 10 0 0 0 12 22Z" fill="#34A853" />
      <path d="M6.4 13.8a6 6 0 0 1 0-3.6V7.7H3.1a10 10 0 0 0 0 8.6l3.3-2.5Z" fill="#FBBC05" />
      <path d="M12 6.1c1.5 0 2.8.5 3.8 1.5l2.9-2.9A9.7 9.7 0 0 0 12 2 10 10 0 0 0 3.1 7.7l3.3 2.5A5.9 5.9 0 0 1 12 6.1Z" fill="#EA4335" />
    </svg>
  );
}

type LoginStatus = "idle" | "submitting-google" | "submitting-email" | "success" | "error";

type LoginPageState = {
  email: string;
  emailFallbackOpen: boolean;
  messageKey: string;
  password: string;
  status: LoginStatus;
};

type LoginPageAction =
  | { type: "error"; messageKey: string }
  | { type: "hideEmailFallback" }
  | { type: "setEmail"; value: string }
  | { type: "setPassword"; value: string }
  | { type: "showEmailFallback" }
  | { type: "submit"; target: "email" | "google" }
  | { type: "success"; messageKey?: string };

const initialLoginPageState: LoginPageState = {
  email: "",
  emailFallbackOpen: false,
  messageKey: "",
  password: "",
  status: "idle",
};

function loginPageReducer(state: LoginPageState, action: LoginPageAction): LoginPageState {
  switch (action.type) {
    case "error":
      return { ...state, messageKey: action.messageKey, status: "error" };
    case "hideEmailFallback":
      return { ...state, emailFallbackOpen: false, messageKey: "", status: "idle" };
    case "setEmail":
      return { ...state, email: action.value };
    case "setPassword":
      return { ...state, password: action.value };
    case "showEmailFallback":
      return { ...state, emailFallbackOpen: true, messageKey: "", status: "idle" };
    case "submit":
      return {
        ...state,
        messageKey: "",
        status: action.target === "google" ? "submitting-google" : "submitting-email",
      };
    case "success":
      return { ...state, messageKey: action.messageKey ?? "", status: "success" };
  }
}

function LoginPage() {
  const { t } = useI18n();
  const [state, dispatch] = useReducer(loginPageReducer, initialLoginPageState);
  const googleSubmitting = state.status === "submitting-google";
  const emailSubmitting = state.status === "submitting-email";
  const isSubmitting = googleSubmitting || emailSubmitting;

  const handleGoogleAuth = async () => {
    dispatch({ type: "submit", target: "google" });

    const authClient = await getAuthClient();

    if (!neonAuthConfigured || !authClient) {
      dispatch({ type: "error", messageKey: "Google sign-in is not ready yet." });
      return;
    }

    const appUrl = new URL("/app", window.location.origin).toString();
    const result = await authClient.signIn.social({
      callbackURL: appUrl,
      errorCallbackURL: new URL("/login", window.location.origin).toString(),
      newUserCallbackURL: appUrl,
      provider: "google",
      requestSignUp: true,
    }).catch(() => ({
      error: {
        message: "Google sign-in failed.",
      },
    }));

    if (result.error) {
      dispatch({ type: "error", messageKey: "Google sign-in failed. Try again." });
      return;
    }

    rememberAuthResult(result);
  };

  const handleEmailSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    dispatch({ type: "submit", target: "email" });

    const authClient = await getAuthClient();

    if (!neonAuthConfigured || !authClient) {
      dispatch({ type: "error", messageKey: "Email sign-in is not ready yet." });
      return;
    }

    const result = await authClient.signIn.email({
      email: state.email.trim(),
      password: state.password,
      rememberMe: true,
    }).catch(() => ({
      error: {
        message: "Email sign-in failed.",
      },
    }));

    if (result.error) {
      dispatch({ type: "error", messageKey: "Email sign-in failed. Check the email and password." });
      return;
    }

    rememberAuthResult(result);
    const token = await waitForAuthToken();
    if (!token) {
      dispatch({ type: "error", messageKey: "We could not finish starting your session. Refresh the page and try again." });
      return;
    }

    dispatch({ type: "success" });
    window.location.assign(new URL("/app", window.location.origin).toString());
  };

  return (
    <div className="login-page auth-google-page">
      <Link className="auth-google-brand" to="/">
        <BrandLogo />
      </Link>
      <main className="auth-google-stage">
        <Surface className={cn("login-card auth-google-card", state.emailFallbackOpen && "is-email-open")}>
          <div className="auth-google-card-header">
            <GoogleLogoIcon className="size-6" />
            <span>{t("Use your Google Account")}</span>
          </div>
          <div className="auth-google-card-body">
            <section className="auth-google-intro">
              <h1>{t("Continue to Attenda")}</h1>
              <p>{t("Use Google to sign in or create your account.")}</p>
            </section>
            <section className="auth-google-actions" aria-label={t("Account access")}>
              {state.messageKey ? <p className={cn("auth-message", state.status === "success" ? "is-success" : "is-error")}>{t(state.messageKey)}</p> : null}
              <button className="login-button auth-google-primary" disabled={isSubmitting} onClick={() => void handleGoogleAuth()} type="button">
                <GoogleLogoIcon className="size-5 shrink-0" />
                <span>{googleSubmitting ? t("Opening Google") : t("Continue with Google")}</span>
                <ArrowRight className="size-4" />
              </button>
              <p className="auth-google-help-text">
                {t("Already have an account? We will sign you in. New to Attenda? We will create your account and continue setup.")}
              </p>
              <div className="auth-email-fallback">
                {state.emailFallbackOpen ? (
                  <div className="grid gap-3">
                    <div className="auth-email-header">
                      <div>
                        <p className="auth-email-title">{t("Email sign-in")}</p>
                        <p className="auth-email-note">{t("For existing email users only. New accounts should continue with Google.")}</p>
                      </div>
                      <button className="auth-secondary-link auth-secondary-button" onClick={() => dispatch({ type: "hideEmailFallback" })} type="button">
                        {t("Hide email sign-in")}
                      </button>
                    </div>
                    <form className="auth-email-form" onSubmit={handleEmailSignIn}>
                      <label>
                        <span>{t("Email")}</span>
                        <input
                          aria-label={t("Email")}
                          autoComplete="email"
                          id="auth-email"
                          name="email"
                          onChange={(event) => dispatch({ type: "setEmail", value: event.target.value })}
                          placeholder={t("Email address")}
                          required
                          spellCheck={false}
                          type="email"
                          value={state.email}
                        />
                      </label>
                      <label>
                        <span>{t("Password")}</span>
                        <input
                          aria-label={t("Password")}
                          autoComplete="current-password"
                          id="auth-password"
                          name="password"
                          onChange={(event) => dispatch({ type: "setPassword", value: event.target.value })}
                          placeholder={t("Password")}
                          required
                          type="password"
                          value={state.password}
                        />
                      </label>
                      <button className="login-button" disabled={isSubmitting} type="submit">
                        {emailSubmitting ? t("Signing in") : t("Sign in")}
                        <ArrowRight className="size-4" />
                      </button>
                      <Link className="auth-secondary-link auth-inline-link" to="/reset-password">{t("Forgot password?")}</Link>
                    </form>
                  </div>
                ) : (
                  <button className="auth-secondary-link auth-secondary-button" onClick={() => dispatch({ type: "showEmailFallback" })} type="button">
                    {t("Sign in with email instead")}
                  </button>
                )}
              </div>
            </section>
          </div>
        </Surface>
      </main>
      <footer className="auth-google-footer">
        <LanguageSwitcher className="auth-google-language" tone="plain" />
        <nav aria-label={t("Account help")}>
          <a href="mailto:hello@attenda.ai?subject=Attenda%20account%20help">{t("Help")}</a>
          <Link to="/">{t("Privacy")}</Link>
          <Link to="/">{t("Terms")}</Link>
        </nav>
      </footer>
    </div>
  );
}

type ResetPasswordStatus = "idle" | "submitting" | "success" | "error";

type ResetPasswordState = {
  confirmPassword: string;
  email: string;
  messageKey: string;
  password: string;
  status: ResetPasswordStatus;
};

type ResetPasswordAction =
  | { type: "clearMessage" }
  | { type: "completeSuccess" }
  | { type: "requestSuccess" }
  | { type: "setConfirmPassword"; value: string }
  | { type: "setEmail"; value: string }
  | { type: "setError"; messageKey: string }
  | { type: "setPassword"; value: string }
  | { type: "submit" };

const initialResetPasswordState: ResetPasswordState = {
  confirmPassword: "",
  email: "",
  messageKey: "",
  password: "",
  status: "idle",
};

function resetPasswordReducer(state: ResetPasswordState, action: ResetPasswordAction): ResetPasswordState {
  if (action.type === "setEmail") return { ...state, email: action.value };
  if (action.type === "setPassword") return { ...state, password: action.value };
  if (action.type === "setConfirmPassword") return { ...state, confirmPassword: action.value };
  if (action.type === "clearMessage") return { ...state, messageKey: "" };
  if (action.type === "submit") return { ...state, messageKey: "", status: "submitting" };
  if (action.type === "setError") return { ...state, messageKey: action.messageKey, status: "error" };
  if (action.type === "requestSuccess") {
    return { ...state, messageKey: "If this email has access, a password reset link has been sent.", status: "success" };
  }
  return { ...state, messageKey: "Password updated. You can sign in with the new password.", status: "success" };
}

function ResetPasswordPage() {
  const { t } = useI18n();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ""));
  const resetToken =
    searchParams.get("token") ??
    searchParams.get("resetToken") ??
    hashParams.get("token") ??
    hashParams.get("resetToken") ??
    "";
  const [{ confirmPassword, email, messageKey, password, status }, dispatchResetPassword] = useReducer(resetPasswordReducer, initialResetPasswordState);

  const requestReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    dispatchResetPassword({ type: "clearMessage" });

    const authClient = await getAuthClient();

    if (!neonAuthConfigured || !authClient) {
      dispatchResetPassword({ messageKey: "Password reset is not ready for this business yet.", type: "setError" });
      return;
    }

    dispatchResetPassword({ type: "submit" });
    const result = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    }).catch(() => ({
      error: {
        message: "We could not send a reset email.",
      },
    }));

    if (result.error) {
      dispatchResetPassword({ messageKey: "We could not send a reset email.", type: "setError" });
      return;
    }

    dispatchResetPassword({ type: "requestSuccess" });
  };

  const completeReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    dispatchResetPassword({ type: "clearMessage" });

    const authClient = await getAuthClient();

    if (!neonAuthConfigured || !authClient) {
      dispatchResetPassword({ messageKey: "Password reset is not ready for this business yet.", type: "setError" });
      return;
    }

    if (password.length < 8) {
      dispatchResetPassword({ messageKey: "Use at least 8 characters for the new password.", type: "setError" });
      return;
    }

    if (password !== confirmPassword) {
      dispatchResetPassword({ messageKey: "The passwords do not match.", type: "setError" });
      return;
    }

    dispatchResetPassword({ type: "submit" });
    const result = await authClient.resetPassword({
      newPassword: password,
      token: resetToken,
    }).catch(() => ({
      error: {
        message: "We could not reset the password.",
      },
    }));

    if (result.error) {
      dispatchResetPassword({ messageKey: "We could not reset the password.", type: "setError" });
      return;
    }

    dispatchResetPassword({ type: "completeSuccess" });
  };

  return (
    <div className="login-page auth-google-page auth-reset-page">
      <Link className="auth-google-brand" to="/">
        <BrandLogo />
      </Link>
      <main className="auth-google-stage">
        <Surface className="login-card auth-google-card auth-reset-card">
          <div className="auth-google-card-header">
            <KeyRound className="size-6 text-slate-900" />
            <span>{t("Account recovery")}</span>
          </div>
          <div className="auth-google-card-body">
            <section className="auth-google-intro">
              <h1>{resetToken ? t("Set a new password") : t("Reset your password")}</h1>
              <p>
                {resetToken
                  ? t("Choose a new password for your Attenda account.")
                  : t("Enter the email used for your invited account and we will send a reset link.")}
              </p>
            </section>

            <section className="auth-google-actions" aria-label={t("Account recovery")}>
              {resetToken ? (
                <form className="auth-email-form" onSubmit={completeReset}>
                  <input
                    aria-hidden="true"
                    autoComplete="username"
                    className="auth-hidden-input"
                    id="reset-username"
                    name="email"
                    readOnly
                    tabIndex={-1}
                    type="email"
                    value={email}
                  />
                  <label>
                    <span>{t("New password")}</span>
                    <input
                      aria-label={t("New password")}
                      autoComplete="new-password"
                      id="reset-password"
                      name="new-password"
                      onChange={(event) => dispatchResetPassword({ type: "setPassword", value: event.target.value })}
                      placeholder={t("New password")}
                      required
                      type="password"
                      value={password}
                    />
                  </label>
                  <label>
                    <span>{t("Confirm password")}</span>
                    <input
                      aria-label={t("Confirm password")}
                      autoComplete="new-password"
                      id="reset-confirm-password"
                      name="confirm-password"
                      onChange={(event) => dispatchResetPassword({ type: "setConfirmPassword", value: event.target.value })}
                      placeholder={t("Confirm password")}
                      required
                      type="password"
                      value={confirmPassword}
                    />
                  </label>
                  {messageKey ? <p className={cn("auth-message", status === "success" ? "is-success" : "is-error")}>{t(messageKey)}</p> : null}
                  <button className="login-button" disabled={status === "submitting"} type="submit">
                    {status === "submitting" ? t("Updating password") : t("Update password")}
                    <ArrowRight className="size-4" />
                  </button>
                </form>
              ) : (
                <form className="auth-email-form" onSubmit={requestReset}>
                  <label>
                    <span>{t("Email")}</span>
                    <input
                      aria-label={t("Email")}
                      autoComplete="email"
                      id="reset-email"
                      name="email"
                      onChange={(event) => dispatchResetPassword({ type: "setEmail", value: event.target.value })}
                      placeholder={t("Email address")}
                      required
                      spellCheck={false}
                      type="email"
                      value={email}
                    />
                  </label>
                  {messageKey ? <p className={cn("auth-message", status === "success" ? "is-success" : "is-error")}>{t(messageKey)}</p> : null}
                  <button className="login-button" disabled={status === "submitting" || status === "success"} type="submit">
                    {status === "submitting" ? t("Sending reset link") : t("Send reset link")}
                    <ArrowRight className="size-4" />
                  </button>
                </form>
              )}

              <Link className="auth-secondary-link auth-inline-link" to="/login">{t("Back to sign in")}</Link>
            </section>
          </div>
        </Surface>
      </main>
      <footer className="auth-google-footer">
        <LanguageSwitcher className="auth-google-language" tone="plain" />
        <nav aria-label={t("Account help")}>
          <a href="mailto:hello@attenda.ai?subject=Attenda%20account%20help">{t("Help")}</a>
          <Link to="/">{t("Privacy")}</Link>
          <Link to="/">{t("Terms")}</Link>
        </nav>
      </footer>
    </div>
  );
}

function LoadingScreen() {
  const { t } = useI18n();
  const navPlaceholders = Array.from({ length: 9 });
  const metricPlaceholders = Array.from({ length: 4 });
  const rowPlaceholders = Array.from({ length: 4 });

  return (
    <div aria-busy="true" aria-live="polite" className="min-h-[100dvh] bg-slate-50 text-slate-950">
      <span className="sr-only">{t("Loading business")}</span>
      <div className="grid min-h-[100dvh] lg:grid-cols-[288px_1fr]">
        <aside className="hidden border-r border-slate-200 bg-white/80 p-5 lg:grid lg:grid-rows-[auto_1fr_auto]">
          <BrandLogo />
          <div className="mt-10 grid content-start gap-2">
            {navPlaceholders.map((_, index) => (
              <div className="flex h-11 items-center gap-3 rounded-lg px-3" key={index}>
                <span className="size-4 rounded-md bg-slate-200/80" />
                <span className={cn("h-3 rounded-full bg-slate-200/80", index % 3 === 0 ? "w-28" : "w-36")} />
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="h-4 w-32 rounded-full bg-slate-200/90" />
              <span className="h-5 w-14 rounded-full bg-emerald-100" />
            </div>
            <span className="mt-4 block h-2 rounded-full bg-slate-200" />
            <span className="mt-3 block h-3 w-20 rounded-full bg-slate-200/80" />
          </div>
        </aside>
        <main className="min-w-0">
          <header className="border-b border-slate-200 bg-white/90 px-4 py-4 sm:px-6 lg:px-8">
            <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-4">
              <div className="grid gap-2">
                <span className="h-3 w-36 rounded-full bg-slate-200/80" />
                <span className="h-5 w-48 rounded-full bg-slate-200" />
              </div>
              <div className="hidden items-center gap-3 sm:flex">
                <span className="h-10 w-56 rounded-lg border border-slate-200 bg-white" />
                <span className="h-10 w-32 rounded-lg border border-slate-200 bg-white" />
                <span className="size-10 rounded-lg border border-slate-200 bg-white" />
              </div>
            </div>
          </header>
          <div className="mx-auto grid w-full max-w-[1400px] gap-6 p-4 sm:p-6 lg:p-8">
            <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <div className="grid gap-8 xl:grid-cols-[1fr_360px] xl:items-center">
                <div className="grid gap-5">
                  <span className="h-6 w-32 rounded-full bg-slate-200/80" />
                  <div className="grid gap-3">
                    <span className="h-8 w-full max-w-xl rounded-full bg-slate-200" />
                    <span className="h-8 w-full max-w-md rounded-full bg-slate-200/80" />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {metricPlaceholders.slice(0, 3).map((_, index) => (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" key={index}>
                        <span className="block h-3 w-20 rounded-full bg-slate-200/80" />
                        <span className="mt-4 block h-7 w-14 rounded-full bg-slate-200" />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                  <span className="block h-4 w-32 rounded-full bg-slate-200/90" />
                  <div className="mt-5 grid gap-3">
                    {rowPlaceholders.slice(0, 3).map((_, index) => (
                      <div className="flex items-center gap-3" key={index}>
                        <span className="size-4 rounded-full bg-emerald-100" />
                        <span className={cn("h-3 rounded-full bg-slate-200/80", index === 0 ? "w-44" : "w-32")} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {metricPlaceholders.map((_, index) => (
                <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" key={index}>
                  <span className="block size-9 rounded-lg bg-slate-100" />
                  <span className="mt-5 block h-3 w-24 rounded-full bg-slate-200/80" />
                  <span className="mt-3 block h-7 w-16 rounded-full bg-slate-200" />
                </div>
              ))}
            </section>
            <section className="grid gap-6 xl:grid-cols-[.9fr_1.1fr]">
              {[0, 1].map((panel) => (
                <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" key={panel}>
                  <span className="block h-5 w-36 rounded-full bg-slate-200" />
                  <div className="mt-5 grid gap-3">
                    {rowPlaceholders.map((_, index) => (
                      <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-100 p-3" key={index}>
                        <div className="grid flex-1 gap-2">
                          <span className="h-3 w-1/2 rounded-full bg-slate-200/90" />
                          <span className="h-3 w-3/4 rounded-full bg-slate-100" />
                        </div>
                        <span className="h-6 w-16 rounded-full bg-slate-100" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

function WorkspaceLoadErrorScreen({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}) {
  const { t } = useI18n();
  const status = error instanceof DashboardRequestError ? error.status : undefined;
  const isForbidden = status === 403;
  const title = isForbidden ? "Business access denied" : "Business could not load";
  const description = isForbidden
    ? "This signed-in account does not have access to the selected business."
    : "We could not load this business right now. Retry before changing accounts.";

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 px-4 py-10">
      <Surface className="w-full max-w-md p-6 shadow-[0_24px_70px_-38px_rgba(15,23,42,0.45)]">
        <div className="flex items-center justify-between gap-4">
          <BrandLogo />
          {status ? <StatusPill tone={isForbidden ? "amber" : "neutral"}>{isForbidden ? t("Access") : t("Retry")}</StatusPill> : null}
        </div>
        <div className="mt-8 grid gap-3">
          <div className="flex size-10 items-center justify-center rounded-lg border border-rose-200 bg-rose-50 text-rose-700">
            <AlertTriangle className="size-5" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">{t(title)}</h1>
          <p className="text-sm leading-6 text-slate-600">{t(description)}</p>
        </div>
        <div className="mt-6 grid gap-2 sm:grid-cols-2">
          <button className="login-button" onClick={onRetry} type="button">
            {t("Retry")}
            <ArrowRight className="size-4" />
          </button>
          <Link className="inline-flex h-11 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50" to="/login">
            {t("Sign in")}
          </Link>
        </div>
      </Surface>
    </div>
  );
}

type PlatformErrorBoundaryProps = {
  area: string;
  children: ReactNode;
};

type PlatformErrorBoundaryState = {
  hasError: boolean;
};

class PlatformErrorBoundary extends Component<PlatformErrorBoundaryProps, PlatformErrorBoundaryState> {
  override state: PlatformErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): PlatformErrorBoundaryState {
    return {
      hasError: true,
    };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[${this.props.area}] runtime error`, error, info);
  }

  override render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <I18nConsumer>
        {({ t }) => (
          <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 px-4 py-10">
            <Surface className="w-full max-w-md p-6 shadow-[0_24px_70px_-38px_rgba(15,23,42,0.45)]">
              <div className="flex items-center justify-between gap-4">
                <BrandLogo />
                <StatusPill tone="amber">{t("Needs attention")}</StatusPill>
              </div>
              <div className="mt-8 grid gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 text-amber-700">
                  <AlertTriangle className="size-5" />
                </div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
                  {this.props.area} {t("needs a refresh")}
                </h1>
                <p className="text-sm leading-6 text-slate-600">{t("Reload the page. If it keeps happening, contact support.")}</p>
              </div>
              <div className="mt-6 grid gap-2 sm:grid-cols-2">
                <button className="login-button" onClick={() => window.location.reload()} type="button">
                  {t("Reload")}
                  <ArrowRight className="size-4" />
                </button>
                <Link className="inline-flex h-11 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50" to="/login">
                  {t("Sign in")}
                </Link>
              </div>
            </Surface>
          </div>
        )}
      </I18nConsumer>
    );
  }
}

function formatAdminInteger(value: number, intlLocale: string) {
  return adminIntegerFormatters[resolveAdminIntlLocale(intlLocale)].format(Math.round(value));
}

function formatAdminCompactInteger(value: number, intlLocale: string) {
  return adminCompactIntegerFormatters[resolveAdminIntlLocale(intlLocale)].format(Math.round(value));
}

function formatAdminDateTime(value: string | undefined, intlLocale: string, t: (key: string) => string) {
  if (!value) return t("Not yet");

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t("Not yet");

  return adminDateTimeFormatters[resolveAdminIntlLocale(intlLocale)].format(date);
}

function formatAdminLabel(value: string) {
  return value
    .replaceAll("_", " ")
    .split(/\s+/u)
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(" ");
}

function adminUsageRoleLabel(role?: UsageSummary["role"]) {
  if (role === "platform_admin") return "Administrator";
  if (role === "owner" || role === "account_owner") return "Owner";
  if (role === "manager") return "Manager";
  if (role === "agent") return "Team member";
  if (role === "viewer") return "Viewer";
  return "Unassigned";
}

type AdminWorkspace = PlatformBootstrap["workspaces"][number];

type AdminLaunchGap = {
  detail: string;
  label: string;
  route: string;
};

function filterAdminWorkspaceItems<T extends { workspaceId: string }>(items: T[], workspace: AdminWorkspace) {
  return items.filter((item) => item.workspaceId === workspace.id);
}

function firstAdminLaunchGap(gaps: ReadonlyArray<AdminLaunchGap | null>) {
  return gaps.find((gap): gap is AdminLaunchGap => Boolean(gap)) ?? null;
}

function adminLaunchGapActionLabel(gap: AdminLaunchGap | null) {
  if (!gap) return "Open";
  if (gap.label === "Business profile") return "Add details";
  if (gap.label === "Caller-facing number") return "Connect number";
  if (gap.label === "Receptionist") return "Publish receptionist";
  if (gap.label === "Approved answers") return "Publish answers";
  if (gap.label === "Booking windows") return "Add hours";
  if (gap.label === "Staff alerts") return "Set alerts";
  if (gap.label === "Team step-in") return "Add team phone";
  return "Open next step";
}

function countAdminReadyNotificationChannels(data: PlatformBootstrap, workspace: AdminWorkspace) {
  const settings = data.notificationSettings.find((item) => item.workspaceId === workspace.id);
  if (!settings) {
    return 0;
  }

  return [
    Boolean(data.project.emailNotificationsConfigured && settings.emailRecipients.length > 0),
    Boolean(settings.slackWebhookUrl),
    Boolean(settings.telegramBotTokenConfigured && settings.telegramChatId),
  ].filter(Boolean).length;
}

function adminDisplayEmail(email: string, workspace?: Pick<AdminWorkspace, "country">) {
  const trimmed = email.trim();
  if (!trimmed.endsWith(".example")) {
    return trimmed;
  }

  const [localPart, rawDomain = "business.example"] = trimmed.split("@");
  const domain = rawDomain.replace(/\.example$/u, "");
  const suffix = workspace?.country === "AU" ? "com.au" : workspace?.country === "US" ? "com" : "co.nz";
  return `${localPart}@${domain}.${suffix}`;
}

function adminWorkspaceLaunchState(data: PlatformBootstrap, workspace: AdminWorkspace) {
  const knowledgeItems = filterAdminWorkspaceItems(data.knowledgeItems, workspace);
  const services = filterAdminWorkspaceItems(data.services, workspace);
  const availabilityRules = filterAdminWorkspaceItems(data.availabilityRules, workspace);
  const staffMembers = filterAdminWorkspaceItems(data.staffMembers, workspace);
  const notificationDeliveries = filterAdminWorkspaceItems(data.notificationDeliveries, workspace);
  const publishedAnswers = knowledgeItems.filter((item) => item.status === "published" && item.active).length;
  const openWindowCount = availabilityRules.filter((rule) => rule.active).length;
  const alertChannelCount = countAdminReadyNotificationChannels(data, workspace);
  const deliveryIssueCount = notificationDeliveries.filter((delivery) => delivery.status !== "delivered").length;
  const liveTransferStaffCount = staffMembers.filter((staff) => staff.active && Boolean(staff.phoneNumber?.trim())).length;
  const activePhoneNumber = data.phoneNumbers.some((phoneNumber) => phoneNumber.workspaceId === workspace.id && phoneNumber.status === "active");
  const activeReceptionist = data.receptionistConfigs.some((config) => config.workspaceId === workspace.id && config.status === "active");
  const businessProfile = data.businessProfiles.some((profile) => profile.workspaceId === workspace.id);
  const firstGap = firstAdminLaunchGap([
    businessProfile
      ? null
      : {
          detail: "Add public business details.",
          label: "Business profile",
          route: "/app/onboard",
        },
    activePhoneNumber
      ? null
      : {
          detail: "Connect a caller-facing number.",
          label: "Caller-facing number",
          route: "/app/phone-numbers",
        },
    activeReceptionist
      ? null
      : {
          detail: "Publish the active receptionist.",
          label: "Receptionist",
          route: "/app/receptionist",
        },
    publishedAnswers > 0 && services.length > 0
      ? null
      : {
          detail: "Publish services and answers callers can rely on.",
          label: "Approved answers",
          route: "/app/knowledge",
        },
    openWindowCount > 0
      ? null
      : {
          detail: "Add the hours callers can book.",
          label: "Booking windows",
          route: "/app/calendar",
        },
    alertChannelCount > 0 && deliveryIssueCount === 0
      ? null
      : {
          detail: alertChannelCount > 0 ? "Fix alert delivery before callers rely on it." : "Choose where the team receives call updates.",
          label: "Staff alerts",
          route: "/app/integrations/staff-notifications",
        },
    liveTransferStaffCount > 0
      ? null
      : {
          detail: "Add a team phone for calls that need a person.",
          label: "Team step-in",
          route: "/app/settings#team",
        },
  ]);
  const checks = [
    businessProfile,
    activePhoneNumber,
    activeReceptionist,
    publishedAnswers > 0 && services.length > 0,
    openWindowCount > 0,
    alertChannelCount > 0 && deliveryIssueCount === 0,
    liveTransferStaffCount > 0,
  ];
  const launchScore = checks.length > 0 ? Math.round((checks.filter(Boolean).length / checks.length) * 100) : 0;
  const progress = Math.max(0, Math.min(100, workspace.setupProgress >= 100 ? launchScore : Math.min(workspace.setupProgress, launchScore)));
  const ready = progress >= 100;

  return {
    actionLabel: workspace.setupProgress < 100 ? "Continue setup" : ready ? "Open" : adminLaunchGapActionLabel(firstGap),
    blockerDetail: firstGap?.detail ?? "Core setup is in place.",
    blockerLabel: firstGap?.label ?? "Ready for live calls",
    progress,
    ready,
    route: workspace.setupProgress < 100 ? "/app/onboard" : ready ? "/app" : firstGap?.route ?? "/app",
  };
}

function adminWorkspaceCallActivity(data: PlatformBootstrap, workspace: AdminWorkspace) {
  const calls = filterAdminWorkspaceItems(data.calls, workspace);
  const latestCallMs = calls.reduce((latest, call) => {
    const startedAtMs = new Date(call.startedAt).getTime();
    return Number.isFinite(startedAtMs) ? Math.max(latest, startedAtMs) : latest;
  }, 0);

  return {
    callCount: calls.length,
    lastCallAt: latestCallMs > 0 ? new Date(latestCallMs).toISOString() : undefined,
    lastCallAtMs: latestCallMs,
  };
}

function rememberAdminSelectedWorkspace(workspaceId: string) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem("attenda.selectedWorkspaceId", workspaceId);
  } catch {
    // Navigation still works; the dashboard will fall back to the first accessible workspace.
  }
}

type AdminWorkspaceFilter = "all" | "needs-action" | "ready";

function AdminPage({ data }: { data: PlatformBootstrap }) {
  const { intlLocale, t } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [workspaceFilter, setWorkspaceFilter] = useState<AdminWorkspaceFilter>("all");

  useEffect(() => {
    document.title = `${t("Businesses")} - Attenda`;
  }, [t]);

  if (data.viewer && !data.viewer.isPlatformAdmin) {
    return <Navigate replace to="/app" />;
  }

  const usageRows = data.usageSummaries
    .slice()
    .sort((left, right) => right.sessions - left.sessions || (right.lastUsedAt ?? "").localeCompare(left.lastUsedAt ?? ""))
    .slice(0, 10);
  const totalConversations = data.usageSummaries.reduce((sum, item) => sum + item.sessions, 0);
  const sevenDaysAgoMs = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const activeUsers = data.usageSummaries.filter((item) => item.lastUsedAt && new Date(item.lastUsedAt).getTime() >= sevenDaysAgoMs).length;
  const workspaceLaunchStates = data.workspaces.map((workspace) => ({
    activity: adminWorkspaceCallActivity(data, workspace),
    state: adminWorkspaceLaunchState(data, workspace),
    workspace,
  })).sort((left, right) => {
    if (left.state.ready !== right.state.ready) {
      return left.state.ready ? 1 : -1;
    }

    if (left.state.progress !== right.state.progress) {
      return left.state.progress - right.state.progress;
    }

    return right.activity.lastCallAtMs - left.activity.lastCallAtMs;
  });
  const readyWorkspaces = workspaceLaunchStates.filter(({ state }) => state.ready).length;
  const needsReviewWorkspaces = workspaceLaunchStates.filter(({ state }) => !state.ready).length;
  const priorityWorkspaceRow = workspaceLaunchStates.find(({ state }) => !state.ready) ?? workspaceLaunchStates[0];
  const normalizedQuery = query.trim().toLowerCase();
  const queryFilteredWorkspaceLaunchStates = normalizedQuery
    ? workspaceLaunchStates.filter(({ workspace }) =>
        [workspace.name, workspace.ownerEmail, adminDisplayEmail(workspace.ownerEmail, workspace), workspace.industryKey, workspace.status]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedQuery)),
      )
    : workspaceLaunchStates;
  const filteredWorkspaceLaunchStates = queryFilteredWorkspaceLaunchStates.filter(({ state }) => {
    if (workspaceFilter === "ready") return state.ready;
    if (workspaceFilter === "needs-action") return !state.ready;
    return true;
  });
  const filteredUsageRows = normalizedQuery
    ? usageRows.filter((row) =>
        [row.userEmail, adminDisplayEmail(row.userEmail, data.workspaces.find((workspace) => workspace.id === row.workspaceId)), row.workspaceName, adminUsageRoleLabel(row.role)]
          .filter(Boolean)
          .some((value) => value?.toLowerCase().includes(normalizedQuery)),
      )
    : usageRows;
  const adminSearchSummary = normalizedQuery || workspaceFilter !== "all"
    ? t("{businesses} businesses / {teamMembers} team members", {
        businesses: formatAdminInteger(filteredWorkspaceLaunchStates.length, intlLocale),
        teamMembers: formatAdminInteger(filteredUsageRows.length, intlLocale),
      })
    : t("Search by business, owner, or team member.");
  const adminFilterOptions: Array<{ count: number; label: string; value: AdminWorkspaceFilter }> = [
    { count: data.workspaces.length, label: "All", value: "all" },
    { count: needsReviewWorkspaces, label: "Needs action", value: "needs-action" },
    { count: readyWorkspaces, label: "Ready", value: "ready" },
  ];
  const noBusinessCopy =
    workspaceFilter === "needs-action" && !normalizedQuery ? t("All visible businesses are ready to answer calls.") : t("No businesses match this search.");

  function openWorkspace(workspace: AdminWorkspace, route: string) {
    rememberAdminSelectedWorkspace(workspace.id);
    navigate(route);
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <Link className="text-sm font-bold text-slate-950" to="/">
          <BrandLogo />
        </Link>
        <div className="flex items-center gap-2">
          <ButtonLink href="#team-activity" tone="soft">{t("Team activity")}</ButtonLink>
          <ButtonLink href="/app" tone="soft">{t("Open business")}</ButtonLink>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        <div className="admin-toolbar mb-8">
          <div className="admin-page-title">
            <SectionLabel>{t("Operations")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold text-slate-950">{t("Businesses")}</h1>
            <p className="mt-3 max-w-2xl text-slate-500">{t("Review call readiness, owners, and customer conversation activity across every business.")}</p>
          </div>
          <div className="admin-search-panel">
            <label className="admin-search-label" htmlFor="admin-business-search">{t("Find a business")}</label>
            <div className="admin-search-control">
              <Search className="size-4" />
              <input
                aria-describedby="admin-search-summary"
                aria-label={t("Search businesses or owners")}
                id="admin-business-search"
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("Search businesses or owners")}
                value={query}
              />
              {query ? (
                <button aria-label={t("Clear search")} className="admin-search-clear" onClick={() => setQuery("")} type="button">
                  <X className="size-4" />
                </button>
              ) : null}
            </div>
            <fieldset className="admin-filter-control">
              <legend className="sr-only">{t("Businesses")}</legend>
              {adminFilterOptions.map((option) => (
                <button
                  aria-pressed={workspaceFilter === option.value}
                  className={cn(workspaceFilter === option.value && "is-active")}
                  key={option.value}
                  onClick={() => setWorkspaceFilter(option.value)}
                  type="button"
                >
                  <span>{t(option.label)}</span>
                  <strong>{formatAdminInteger(option.count, intlLocale)}</strong>
                </button>
              ))}
            </fieldset>
            <p className="admin-search-summary" id="admin-search-summary">{adminSearchSummary}</p>
          </div>
        </div>
        <div className="admin-summary-grid mb-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Surface className="admin-summary-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{t("Businesses")}</p>
            <p className="mt-3 text-3xl font-bold text-slate-950">{formatAdminInteger(data.workspaces.length, intlLocale)}</p>
            <p className="mt-1 text-sm text-slate-500">{t("Businesses under care")}</p>
          </Surface>
          <Surface className="admin-summary-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{t("Ready businesses")}</p>
            <p className="mt-3 text-3xl font-bold text-slate-950">{formatAdminInteger(readyWorkspaces, intlLocale)}</p>
            <p className="mt-1 text-sm text-slate-500">{t("Can handle live callers")}</p>
          </Surface>
          <Surface className="admin-summary-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{t("Needs action")}</p>
            <p className="mt-3 text-3xl font-bold text-slate-950">{formatAdminInteger(needsReviewWorkspaces, intlLocale)}</p>
            <p className="mt-1 text-sm text-slate-500">{t("Next caller-ready item")}</p>
          </Surface>
          <Surface className="admin-summary-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{t("Conversations")}</p>
            <p className="mt-3 text-3xl font-bold text-slate-950">{formatAdminCompactInteger(totalConversations, intlLocale)}</p>
            <p className="mt-1 text-sm text-slate-500">
              {t(activeUsers === 1 ? "{count} active team member this week" : "{count} active team members this week", { count: formatAdminInteger(activeUsers, intlLocale) })}
            </p>
          </Surface>
        </div>
        {priorityWorkspaceRow ? (
          <Surface className="admin-priority-panel mb-6">
            <div className="admin-priority-copy">
              <SectionLabel>{priorityWorkspaceRow.state.ready ? t("Caller-ready coverage") : t("Next business to finish")}</SectionLabel>
              <h2>
                {priorityWorkspaceRow.state.ready
                  ? t("Every business is ready for live callers")
                  : t("Work on {business} next", { business: priorityWorkspaceRow.workspace.name })}
              </h2>
              <p>
                {priorityWorkspaceRow.state.ready
                  ? t("All visible businesses are ready to answer calls.")
                  : t("{need}: {detail}", {
                      detail: t(priorityWorkspaceRow.state.blockerDetail),
                      need: t(priorityWorkspaceRow.state.blockerLabel),
                    })}
              </p>
            </div>
            <div className="admin-priority-facts">
              <div>
                <span>{t("Owner")}</span>
                <strong>{adminDisplayEmail(priorityWorkspaceRow.workspace.ownerEmail, priorityWorkspaceRow.workspace)}</strong>
              </div>
              <div>
                <span>{t("Caller activity")}</span>
                <strong>{t(priorityWorkspaceRow.activity.callCount === 1 ? "{count} call" : "{count} calls", { count: formatAdminInteger(priorityWorkspaceRow.activity.callCount, intlLocale) })}</strong>
                <small>
                  {priorityWorkspaceRow.activity.lastCallAt
                    ? t("Last call {time}", { time: formatAdminDateTime(priorityWorkspaceRow.activity.lastCallAt, intlLocale, t) })
                    : t("No calls yet")}
                </small>
              </div>
              <div>
                <span>{t("Progress")}</span>
                <strong>{priorityWorkspaceRow.state.progress}%</strong>
                <small>{priorityWorkspaceRow.state.ready ? t("Ready") : t("Needs action")}</small>
              </div>
            </div>
            <button
              aria-label={t("{action} for {business}", { action: t(priorityWorkspaceRow.state.actionLabel), business: priorityWorkspaceRow.workspace.name })}
              className="admin-row-action admin-priority-action"
              onClick={() => openWorkspace(priorityWorkspaceRow.workspace, priorityWorkspaceRow.state.route)}
              type="button"
            >
              {t(priorityWorkspaceRow.state.actionLabel)}
              <ArrowRight className="size-4" />
            </button>
          </Surface>
        ) : null}
        <Surface className="admin-table-surface overflow-hidden">
          <div className="hidden min-w-[1040px] grid-cols-[1.05fr_.92fr_.72fr_.76fr_.64fr_auto] gap-4 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 md:grid">
            <span>{t("Business")}</span>
            <span>{t("Next step")}</span>
            <span>{t("Owner")}</span>
            <span>{t("Caller activity")}</span>
            <span>{t("Progress")}</span>
            <span>{t("Action")}</span>
          </div>
          {filteredWorkspaceLaunchStates.length === 0 ? (
            <div className="admin-empty-state">{noBusinessCopy}</div>
          ) : (
            filteredWorkspaceLaunchStates.map(({ activity, state, workspace }) => {
              const actionLabel = t(state.actionLabel);
              return (
                <div className="admin-account-row grid grid-cols-1 gap-3 border-b border-slate-100 px-5 py-4 text-sm last:border-b-0 md:min-w-[1040px] md:grid-cols-[1.05fr_.92fr_.72fr_.76fr_.64fr_auto] md:items-center md:gap-4" key={workspace.id}>
                  <span className="admin-account-cell">
                    <strong>{workspace.name}</strong>
                    <span>{t(formatAdminLabel(workspace.industryKey))} · {t(workspace.status)}</span>
                  </span>
                  <span className="admin-need-cell">
                    <span className={cn("admin-need-title", state.ready && "is-ready")}>
                      <StatusPill tone={state.ready ? "green" : "amber"}>{state.ready ? t("Ready") : t("Needs action")}</StatusPill>
                      {t(state.blockerLabel)}
                    </span>
                    <span>{t(state.blockerDetail)}</span>
                  </span>
                  <span className="admin-owner-cell"><span className="font-semibold text-slate-400 md:hidden">{t("Owner")}: </span>{adminDisplayEmail(workspace.ownerEmail, workspace)}</span>
                  <span className="admin-activity-cell">
                    <strong>{t(activity.callCount === 1 ? "{count} call" : "{count} calls", { count: formatAdminInteger(activity.callCount, intlLocale) })}</strong>
                    <span>{activity.lastCallAt ? t("Last call {time}", { time: formatAdminDateTime(activity.lastCallAt, intlLocale, t) }) : t("No calls yet")}</span>
                  </span>
                  <span className="admin-row-progress">
                    <span>{state.progress}%</span>
                    <span aria-hidden="true"><span style={{ width: `${state.progress}%` }} /></span>
                  </span>
                  <button
                    aria-label={t("{action} for {business}", { action: actionLabel, business: workspace.name })}
                    className="admin-row-action"
                    onClick={() => openWorkspace(workspace, state.route)}
                    type="button"
                  >
                    {actionLabel}
                    <ArrowRight className="size-4" />
                  </button>
                </div>
              );
            })
          )}
        </Surface>
        <div id="team-activity">
          <Surface className="admin-table-surface mt-6 overflow-hidden">
            <div className="border-b border-slate-100 bg-slate-50 px-5 py-4">
              <h2 className="text-sm font-bold text-slate-950">{t("Team activity")}</h2>
              <p className="mt-1 text-sm text-slate-500">{t("Recent customer conversations by team member and business.")}</p>
            </div>
            {filteredUsageRows.length === 0 ? (
              <div className="admin-empty-state">{normalizedQuery ? t("No team members match this search.") : t("Conversation activity will appear after the first live call or receptionist test.")}</div>
            ) : (
              <>
                <div className="hidden min-w-[860px] grid-cols-[1.1fr_1fr_.55fr_.75fr_auto] gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 md:grid">
                  <span>{t("Team member")}</span>
                  <span>{t("Business")}</span>
                  <span className="text-right">{t("Conversations")}</span>
                  <span>{t("Last active")}</span>
                  <span>{t("Action")}</span>
                </div>
                {filteredUsageRows.map((row) => {
                  const activityWorkspace = row.workspaceId ? data.workspaces.find((workspace) => workspace.id === row.workspaceId) : undefined;

                  const displayUserEmail = adminDisplayEmail(row.userEmail, activityWorkspace);

                  return (
                    <div className="grid grid-cols-1 gap-2 border-b border-slate-100 px-5 py-4 text-sm last:border-b-0 md:min-w-[860px] md:grid-cols-[1.1fr_1fr_.55fr_.75fr_auto] md:items-center md:gap-3" key={row.id}>
                      <span className="min-w-0">
                        <strong className="block truncate text-slate-950">{displayUserEmail}</strong>
                        <span className="block truncate text-xs text-slate-500">{t(adminUsageRoleLabel(row.role))}</span>
                      </span>
                      <span className="truncate text-slate-500"><span className="font-semibold text-slate-400 md:hidden">{t("Business")}: </span>{row.workspaceName ?? t("No business")}</span>
                      <span className="text-slate-500 md:text-right"><span className="font-semibold text-slate-400 md:hidden">{t("Conversations")}: </span>{formatAdminInteger(row.sessions, intlLocale)}</span>
                      <span className="text-slate-500"><span className="font-semibold text-slate-400 md:hidden">{t("Last active")}: </span>{formatAdminDateTime(row.lastUsedAt, intlLocale, t)}</span>
                      <span className="admin-usage-open-cell">
                        <button
                          aria-label={activityWorkspace ? t("Review {business}", { business: activityWorkspace.name }) : t("No business")}
                          className="admin-row-action admin-usage-open-action"
                          disabled={!activityWorkspace}
                          onClick={() => {
                            if (activityWorkspace) {
                              openWorkspace(activityWorkspace, "/app/analytics");
                            }
                          }}
                          type="button"
                        >
                          {activityWorkspace ? t("Review business") : t("No business")}
                          {activityWorkspace ? <ArrowRight className="size-4" /> : null}
                        </button>
                      </span>
                    </div>
                  );
                })}
              </>
            )}
          </Surface>
        </div>
      </main>
    </div>
  );
}

export function App() {
  const { pathname, search } = useLocation();
  const [canonicalizingLocalhost] = useState(() => window.location.hostname === "127.0.0.1");
  const [data, setData] = useState<PlatformBootstrap | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const needsBootstrap = pathname.startsWith("/app") || pathname.startsWith("/admin");

  useEffect(() => {
    if (!canonicalizingLocalhost) {
      return;
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.hostname = "localhost";
    window.location.replace(nextUrl.toString());
  }, [canonicalizingLocalhost]);

  useEffect(() => {
    if (canonicalizingLocalhost || !needsBootstrap) {
      return;
    }

    let cancelled = false;
    setLoadError(null);
    void fetchBootstrap({ requireAuth: pathname.startsWith("/app") || pathname.startsWith("/admin") })
      .then((bootstrap) => {
        if (!cancelled) {
          setData(bootstrap);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setData(null);
          setLoadError(error instanceof Error ? error : new Error("Business could not load."));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canonicalizingLocalhost, pathname, needsBootstrap, retryKey]);

  if (canonicalizingLocalhost) {
    return <LoadingScreen />;
  }

  if ((pathname.startsWith("/app") || pathname.startsWith("/admin")) && loadError instanceof DashboardRequestError && loadError.status === 401) {
    return <Navigate replace to={protectedLoginPath()} />;
  }

  return (
    <Routes>
      <Route
        element={
          <PlatformErrorBoundary area="Public site">
            <Suspense fallback={<PublicHomeLoadingScreen />}>
              <PublicHome />
            </Suspense>
          </PlatformErrorBoundary>
        }
        path="/"
      />
      <Route element={<LoginPage key={`${pathname}${search}`} />} path="/login" />
      <Route element={<LoginPage key={`${pathname}${search}`} />} path="/signup" />
      <Route element={<ResetPasswordPage />} path="/reset-password" />
      <Route
        element={
          data ? (
            <PlatformErrorBoundary area="Dashboard">
              <Suspense fallback={<LoadingScreen />}>
                <DashboardApp data={data} />
              </Suspense>
            </PlatformErrorBoundary>
          ) : loadError ? (
            <WorkspaceLoadErrorScreen error={loadError} onRetry={() => setRetryKey((current) => current + 1)} />
          ) : (
            <LoadingScreen />
          )
        }
        path="/app/*"
      />
      <Route
        element={
          data ? (
            <PlatformErrorBoundary area="Admin">
              <AdminPage data={data} />
            </PlatformErrorBoundary>
          ) : loadError ? (
            <WorkspaceLoadErrorScreen error={loadError} onRetry={() => setRetryKey((current) => current + 1)} />
          ) : (
            <LoadingScreen />
          )
        }
        path="/admin"
      />
      <Route element={<Navigate replace to="/" />} path="*" />
    </Routes>
  );
}
